import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Slider,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import { MapPin, Map, X } from 'lucide-react';

const GeometryTools = ({
  onGeometryChange,
  radius,
  onRadiusChange,
}) => {
  const [activeMode, setActiveMode] = useState('none');
  const [geometries, setGeometries] = useState([]);
  const [showRadiusConfig, setShowRadiusConfig] = useState(false);
  const [showGeographyMenu, setShowGeographyMenu] = useState(false);
  const [radiusValue, setRadiusValue] = useState(radius || 5);
  const [selectedRings, setSelectedRings] = useState([1, 3, 5]);
  const [geographyAnchor, setGeographyAnchor] = useState(null);

  useEffect(() => {
    if (radius !== undefined && radius !== radiusValue) {
      setRadiusValue(radius);
    }
  }, [radius, radiusValue]);

  const addRadiusGeometry = () => {
    const newGeometry = {
      id: `radius-${Date.now()}`,
      type: 'radius',
      name: `${radiusValue} mi radius`,
      center: [-96.797, 32.7767],
      radius: radiusValue,
      rings: selectedRings,
    };
    // Only one radius at a time: replace any existing radius with the latest
    const updated = [...geometries.filter(g => g.type !== 'radius'), newGeometry];
    setGeometries(updated);
    onGeometryChange?.(updated);
    setShowRadiusConfig(false);
    setActiveMode('none');
  };

  const addCensusGeometry = (level, name, id) => {
    const newGeometry = {
      id: `census-${Date.now()}`,
      type: 'census',
      name: name,
      censusLevel: level,
      censusId: id,
      area: level === 'zip' ? 15.2 : level === 'tract' ? 1.8 : 0.3,
      population: level === 'zip' ? 45000 : level === 'tract' ? 4200 : 800,
    };
    const updated = [...geometries, newGeometry];
    setGeometries(updated);
    onGeometryChange?.(updated);
    setShowGeographyMenu(false);
    setGeographyAnchor(null);
  };

  const removeGeometry = id => {
    const updated = geometries.filter(g => g.id !== id);
    setGeometries(updated);
    onGeometryChange?.(updated);
  };

  const toggleRing = ring => {
    setSelectedRings(prev =>
      prev.includes(ring)
        ? prev.filter(r => r !== ring)
        : [...prev, ring].sort((a, b) => a - b),
    );
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'visible' }}>
      <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Button
          variant={activeMode === 'radius' ? 'contained' : 'outlined'}
          size='small'
          onClick={() => {
            setActiveMode(activeMode === 'radius' ? 'none' : 'radius');
            setShowRadiusConfig((prev) => !prev);
          }}
          startIcon={<MapPin size={16} />}
          sx={{
            textTransform: 'none',
            ...(activeMode === 'radius' && {
              backgroundColor: '#39585C',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#2d4649',
                color: '#FFFFFF',
              },
            }),
            borderColor: '#39585C',
            color: activeMode === 'radius' ? '#FFFFFF' : '#39585C',
            '&:hover': {
              borderColor: '#2d4649',
              backgroundColor: activeMode === 'radius' ? '#2d4649' : '#F0F4F5',
              color: activeMode === 'radius' ? '#FFFFFF' : '#39585C',
            },
            fontSize: { xs: '0.75rem', md: '0.875rem' },
          }}
        >
          Radius
        </Button>

        <Button
          variant='outlined'
          size='small'
          onClick={e => {
            setGeographyAnchor(e.currentTarget);
            setShowGeographyMenu(!showGeographyMenu);
          }}
          startIcon={<Map size={16} />}
          sx={{
            textTransform: 'none',
            borderColor: '#D1D5DB',
            color: '#111827',
            '&:hover': {
              borderColor: '#39585C',
              backgroundColor: '#F0F4F5',
            },
            fontSize: { xs: '0.75rem', md: '0.875rem' },
          }}
        >
          Geography
        </Button>
      </Box>

      {showRadiusConfig && (
        <Card
          sx={{
            position: 'absolute',
            top: { xs: '2.5rem', md: '3rem' },
            left: 0,
            width: { xs: '280px', sm: '320px' },
            maxWidth: { xs: 'calc(100vw - 2rem)', sm: '320px' },
            padding: { xs: '0.75rem', md: '1rem' },
            boxShadow: 3,
            zIndex: 9999,
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                Radius Selection
              </Typography>
              <IconButton
                size='small'
                onClick={() => setShowRadiusConfig(false)}
                sx={{ padding: '0.25rem' }}
              >
                <X size={16} />
              </IconButton>
            </Box>

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                  Radius
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  {radiusValue} mi
                </Typography>
              </Box>
              <Slider
                value={radiusValue}
                onChange={(e, value) => {
                  setRadiusValue(value);
                  onRadiusChange?.(value);
                }}
                min={0.25}
                max={20}
                step={0.25}
                sx={{ width: '100%',
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#39585C',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#39585C',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#E5E7EB',
                  },
                  '& .MuiSlider-valueLabel': {
                    backgroundColor: '#39585C',
                  },
                  '& .MuiSlider-valueLabelOpen': {
                    backgroundColor: '#39585C',
                  },
                  '& .MuiSlider-valueLabelOpen:after': {
                    borderColor: '#39585C',
                  },
                  '& .MuiSlider-valueLabelOpen:before': {
                    borderColor: '#39585C',
                  },
          
                 }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  marginBottom: '0.5rem',
                }}
              >
                Quick Rings
              </Typography>
              <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                {[1, 3, 5].map(ring => (
                  <Chip
                    key={ring}
                    label={`${ring} mi`}
                    onClick={() => {
                      setRadiusValue(ring);
                      onRadiusChange?.(ring);
                    }}
                    variant={
                      radiusValue === ring ? 'filled' : 'outlined'
                    }
                    size='small'
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            <Button
              variant='contained'
              onClick={addRadiusGeometry}
              fullWidth
              size='small'
              sx={{
                textTransform: 'none',
                backgroundColor: '#39585C',
                '&:hover': {
                  backgroundColor: '#2d4649',
                },
              }}
            >
              Apply Radius
            </Button>
          </Box>
        </Card>
      )}

      <Menu
        anchorEl={geographyAnchor}
        open={Boolean(geographyAnchor)}
        onClose={() => {
          setGeographyAnchor(null);
          setShowGeographyMenu(false);
        }}
        PaperProps={{
          sx: {
            width: { xs: '260px', sm: '288px' },
            maxWidth: { xs: 'calc(100vw - 2rem)', sm: '288px' },
            marginTop: '0.5rem',
          },
        }}
      >
        <Box sx={{ padding: '0.75rem', borderBottom: '1px solid #E5E7EB' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
              Geographic Boundaries
            </Typography>
            <IconButton
              size='small'
              onClick={() => {
                setGeographyAnchor(null);
                setShowGeographyMenu(false);
              }}
              sx={{ padding: '0.25rem' }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        </Box>
        <MenuItem
          onClick={() =>
            addCensusGeometry(
              'block',
              'Census Block 481130201001',
              '481130201001',
            )
          }
          sx={{
            padding: '0.75rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            Census Block
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            Smallest unit (~800 people)
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() =>
            addCensusGeometry('tract', 'Census Tract 201.01', '201.01')
          }
          sx={{
            padding: '0.75rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            Census Tract
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            ~4,200 people
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => addCensusGeometry('zip', 'ZIP Code 75219', '75219')}
          sx={{
            padding: '0.75rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            ZIP Code
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            Postal boundaries
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => addCensusGeometry('city', 'Dallas, TX', 'dallas-tx')}
          sx={{
            padding: '0.75rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            City
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            Municipal boundaries
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() =>
            addCensusGeometry('county', 'Dallas County', 'dallas-county')
          }
          sx={{
            padding: '0.75rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            County
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            County boundaries
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() =>
            addCensusGeometry('msa', 'Dallas-Fort Worth MSA', 'dfw-msa')
          }
          sx={{
            padding: '0.75rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            MSA
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            Metropolitan Statistical Area
          </Typography>
        </MenuItem>
      </Menu>

      {geometries.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          {geometries.map(geo => (
            <GeometryChip
              key={geo.id}
              geometry={geo}
              onRemove={() => removeGeometry(geo.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

function GeometryChip({ geometry, onRemove }) {
  const getIcon = () => {
    switch (geometry.type) {
      case 'radius':
        return <MapPin size={12} />;
      case 'polygon':
        return <span style={{ fontSize: '0.75rem' }}>📍</span>;
      case 'census':
        return <span style={{ fontSize: '0.75rem' }}>📍</span>;
      default:
        return null;
    }
  };

  const getDetails = () => {
    if (geometry.type === 'radius' && geometry.rings) {
      return `${geometry.rings.join('/')} mi rings`;
    }
    if (geometry.type === 'polygon' && geometry.area) {
      return `${geometry.area} sq mi • ${(geometry.population / 1000).toFixed(
        1,
      )}k pop`;
    }
    if (geometry.type === 'census') {
      return `${(geometry.population / 1000).toFixed(1)}k pop`;
    }
    return '';
  };

  return (
    <Chip
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {getIcon()}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
              {geometry.name}
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', color: '#6B7280' }}>
              {getDetails()}
            </Typography>
          </Box>
        </Box>
      }
      onDelete={onRemove}
      size='small'
      sx={{
        height: 'auto',
        padding: '0.375rem 0.25rem',
        '& .MuiChip-deleteIcon': {
          fontSize: '0.75rem',
        },
      }}
    />
  );
}

export default GeometryTools;
